/**
 * Header Construction
 * 
 * Utilities for building HTTP headers for API requests.
 * 
 * Handles:
 * - Client identification headers (X-Client-App, X-Client-Platform)
 * - Tenant context headers (X-Tenant-Id, X-Club-Id)
 * - Authentication headers (Authorization: Bearer <token>)
 * - Custom headers (per request)
 * - Content-Type defaults
 * 
 * @module headers
 */

import { HEADERS, CLIENT_INFO, API_CONFIG, IS_PRODUCTION } from '../constants';
import { getAccessToken } from '~/services/auth/storage/tokenStorage';
import type { RequestOptions } from '../types';
import { isRateBreakdownEnabled } from '~/features/dev-tools/rateBreakdown';

/**
 * Build headers for API request
 * 
 * Combines default headers, client identification, tenant context,
 * authentication, and custom headers.
 * 
 * **Header Priority (highest to lowest):**
 * 1. Custom headers (options.customHeaders)
 * 2. Request headers (options.headers)
 * 3. Default headers (Content-Type, client ID)
 * 
 * @param options - Request options
 * @returns Headers object ready for fetch()
 * 
 * @example
 * ```typescript
 * const headers = buildHeaders({
 *   tenantHandle: 'drivingrangecollective',
 *   clubId: 2,
 *   customHeaders: {
 *     'X-Custom': 'value',
 *   },
 * });
 * 
 * // Result:
 * // {
 * //   'Content-Type': 'application/json',
 * //   'X-Client-App': 'web-portal-2',
 * //   'X-Client-Platform': 'web',
 * //   'X-Tenant-Id': 'drivingrangecollective',
 * //   'X-Club-Id': '2',
 * //   'Authorization': 'Bearer <token>',
 * //   'X-Custom': 'value',
 * // }
 * ```
 */
export function buildHeaders(
  options: RequestOptions = {}
): HeadersInit {
  const headers: Record<string, string> = {};
  
  // 1. Default headers
  headers[HEADERS.CONTENT_TYPE] = 'application/json';
  
  // 2. Client identification headers (for monitoring)
  headers[HEADERS.CLIENT_APP] = CLIENT_INFO.APP;
  headers[HEADERS.CLIENT_PLATFORM] = CLIENT_INFO.PLATFORM;
  
  // 3. Tenant context headers (for monitoring)
  if (options.tenantHandle) {
    headers[HEADERS.TENANT_ID] = options.tenantHandle;
  }
  
  if (options.clubId !== undefined) {
    headers[HEADERS.CLUB_ID] = String(options.clubId);
  }
  
  // 4. Dev-tools detail — off by default and impossible in production. When on,
  // the server includes the pricing rate build-up in a quote. Header-gated
  // rather than environment-gated so every environment runs one server path and
  // only the payload differs.
  if (isRateBreakdownEnabled()) {
    headers['x-butter-dev-tools'] = '1';
  }

  // 5. Authentication header (if not skipped)
  if (!options.skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers[HEADERS.AUTHORIZATION] = `Bearer ${token}`;
    } else if (!IS_PRODUCTION) {
      console.warn(
        '[ApiClient] ⚠️ No access token found',
        '\nRequest will be made without authentication.',
        '\nIf this endpoint requires auth, it will fail with 401.',
        '\nTo skip this warning, use { skipAuth: true }'
      );
    }
  }
  
  // 5. Apply request headers (options.headers)
  if (options.headers) {
    Object.assign(headers, options.headers);
  }
  
  // 6. Apply custom headers (highest priority)
  if (options.customHeaders) {
    Object.assign(headers, options.customHeaders);
  }
  
  return headers;
}

/**
 * Build URL with tenant context query params
 * 
 * Adds tenantId and clubId as query parameters for API middleware.
 * 
 * **Important:** Tenant context is included in BOTH:
 * - URL params (for API middleware)
 * - Headers (for monitoring)
 * 
 * @param baseUrl - Base URL (from API_CONFIG.baseUrl)
 * @param endpoint - API endpoint (e.g., '/bookings')
 * @param options - Request options with tenant context
 * @returns Full URL with query params
 * 
 * @example
 * ```typescript
 * const url = buildUrl(
 *   'http://localhost:3000/api/public',
 *   '/bookings',
 *   {
 *     tenantHandle: 'drivingrangecollective',
 *     clubId: 2,
 *   }
 * );
 * 
 * // Result: 'http://localhost:3000/api/public/bookings?tenantId=drivingrangecollective&clubId=2'
 * ```
 */
export function buildUrl(
  baseUrl: string,
  endpoint: string,
  options: RequestOptions = {}
): string {
  // Handle full URLs (don't modify external URLs)
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }
  
  // Clean up slashes
  const cleanBase = baseUrl.replace(/\/+$/, ''); // Remove trailing slashes
  const cleanEndpoint = endpoint.replace(/^\/+/, ''); // Remove leading slashes
  
  // Construct URL
  const url = new URL(`${cleanBase}/${cleanEndpoint}`);
  
  // Add tenant context as query params
  if (options.tenantHandle) {
    url.searchParams.set('tenantId', options.tenantHandle);
  }
  
  if (options.clubId !== undefined) {
    url.searchParams.set('clubId', String(options.clubId));
  }
  
  return url.toString();
}

/**
 * Build full URL with version prefix and tenant context
 * 
 * Convenience function that adds API version prefix.
 * Supports per-endpoint version override for gradual API migration.
 * 
 * @param endpoint - API endpoint (e.g., '/bookings')
 * @param options - Request options with tenant context and optional version override
 * @returns Full URL with version prefix and query params
 * 
 * @example
 * ```typescript
 * // Default version (/v1)
 * const url = buildVersionedUrl('/bookings', {
 *   tenantHandle: 'drivingrangecollective',
 *   clubId: 2,
 * });
 * // Result: 'http://localhost:3000/api/public/v1/bookings?tenantId=drivingrangecollective&clubId=2'
 * 
 * // Override to /v2 for specific endpoint
 * const urlV2 = buildVersionedUrl('/bookings', {
 *   tenantHandle: 'drivingrangecollective',
 *   clubId: 2,
 *   apiVersion: '/v2',
 * });
 * // Result: 'http://localhost:3000/api/public/v2/bookings?tenantId=drivingrangecollective&clubId=2'
 * ```
 */
export function buildVersionedUrl(
  endpoint: string,
  options: RequestOptions = {}
): string {
  // Don't add version prefix to full URLs
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return buildUrl(API_CONFIG.baseUrl, endpoint, options);
  }
  
  // Use version override or default version
  const version = options.apiVersion ?? API_CONFIG.version;
  
  // Add version prefix
  const versionedEndpoint = `${version}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  
  return buildUrl(API_CONFIG.baseUrl, versionedEndpoint, options);
}

/**
 * Extract tenant context from URL
 * 
 * Useful for extracting tenant info from current page URL.
 * 
 * @param url - URL to extract from (default: window.location.href)
 * @returns Tenant context or undefined
 * 
 * @example
 * ```typescript
 * // URL: /bookings?tenantId=drivingrangecollective&clubId=2
 * const context = extractTenantContext();
 * // { tenantHandle: 'drivingrangecollective', clubId: 2 }
 * ```
 */
export function extractTenantContext(url?: string): {
  tenantHandle?: string;
  clubId?: number;
} | undefined {
  if (typeof window === 'undefined' && !url) {
    return undefined;
  }
  
  const targetUrl = url || window.location.href;
  
  try {
    const urlObj = new URL(targetUrl);
    const tenantId = urlObj.searchParams.get('tenantId');
    const clubIdStr = urlObj.searchParams.get('clubId');
    
    if (!tenantId && !clubIdStr) {
      return undefined;
    }
    
    return {
      tenantHandle: tenantId || undefined,
      clubId: clubIdStr ? parseInt(clubIdStr, 10) : undefined,
    };
  } catch {
    return undefined;
  }
}
